import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bell, CheckCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 30000;

function tiempoRelativo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "ahora";
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} d`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notificaciones/unread-count`);
      setUnread(res.data.count || 0);
    } catch (e) {
      // silencioso: campanita no crítica
    }
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notificaciones`, { params: { limit: 10 } });
      setNotifs(res.data || []);
    } catch (e) {
      toast.error("No se pudieron cargar las notificaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling del contador
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  // Al abrir el dropdown, recarga lista
  useEffect(() => {
    if (open) {
      refreshList();
    }
  }, [open, refreshList]);

  const handleClickNotif = async (notif) => {
    try {
      if (!notif.leida) {
        await axios.post(`${API}/notificaciones/${notif.id}/read`);
      }
    } catch (e) {
      // no bloquea navegación
    }
    setOpen(false);
    refreshCount();
    if (notif.action_url) {
      navigate(notif.action_url);
    }
  };

  const handleMarkAll = async () => {
    try {
      await axios.post(`${API}/notificaciones/read-all`);
      toast.success("Todas marcadas como leídas");
      setUnread(0);
      refreshList();
    } catch (e) {
      toast.error("Error al marcar como leídas");
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notificaciones"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-semibold text-sm">Notificaciones</span>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleMarkAll}
              data-testid="mark-all-read"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Marcar todas
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[400px]">
          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Cargando…</div>
          ) : notifs.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No tienes notificaciones
            </div>
          ) : (
            <div className="divide-y">
              {notifs.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClickNotif(n)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 hover:bg-accent transition-colors flex gap-2",
                    !n.leida && "bg-accent/40"
                  )}
                  data-testid={`notification-${n.id}`}
                >
                  <span
                    className={cn(
                      "mt-1 h-2 w-2 rounded-full shrink-0",
                      n.leida ? "bg-transparent" : "bg-primary"
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{n.titulo}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {tiempoRelativo(n.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {n.mensaje}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
